import { Box, Button, Center, Group, Loader, Stack, Switch, Text } from '@mantine/core';
import type { CityContentMapRO } from '@routellers/types/dist/city/city-content-map.ro';
import maplibregl from 'maplibre-gl';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { enumLabel } from '../i18n';
import {
    DISTRICT_FILL_BELOW_LAYER_ID,
    DISTRICT_WASH_OPACITY,
    getRoutellersMapStyle,
    ROUTE_LINE_COLOR,
} from '../utils/app-map-style';
import { fetchWalkingRoute } from '../utils/walk-route';
import { CityMapPinCard } from './city-map-pin-card';

import 'maplibre-gl/dist/maplibre-gl.css';

type Props = {
    cityId: number;
};

type LngLat = [number, number];

type FeatureProps = Record<string, string | number>;

type MapFeature = {
    type: 'Feature';
    properties: FeatureProps;
    geometry:
        | { type: 'Polygon'; coordinates: LngLat[][] }
        | { type: 'LineString'; coordinates: LngLat[] }
        | { type: 'Point'; coordinates: LngLat };
};

type MapCollection = {
    type: 'FeatureCollection';
    features: MapFeature[];
};

const PAPER = '#E8E9ED';
const PIN_ON_ROUTE = '#7C3AED';
const PIN_OFF_ROUTE = '#EAB308';
const PIN_CAFE = '#15803D';

type SelectedPin = {
    kind: 'place' | 'cafe';
    id: number;
};

type MapActions = {
    select: (kind: 'route' | 'district', id: number) => void;
    clear: () => void;
    pin: (kind: 'place' | 'cafe', id: number) => void;
};

const DISTRICT_COLORS = [
    '#0F766E',
    '#2563EB',
    '#7C3AED',
    '#B45309',
    '#BE123C',
    '#0369A1',
    '#4D7C0F',
    '#9333EA',
];

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const LAYERS = {
    districts: 'city-districts',
    districtFill: 'city-districts-fill',
    districtPoints: 'city-districts-points',
    routes: 'city-routes',
    routeHit: 'city-routes-hit',
    routePoints: 'city-route-points',
    places: 'city-places',
    cafes: 'city-cafes',
} as const;

const INTERACTIVE = [
    LAYERS.cafes,
    LAYERS.places,
    LAYERS.routePoints,
    LAYERS.routes,
    LAYERS.routeHit,
    LAYERS.districtPoints,
    LAYERS.districtFill,
];

const LegendDot = ({ color, label }: { color: string; label: string }) => (
    <Group gap={6} wrap='nowrap'>
        <span
            style={{
                width: 8,
                height: 8,
                borderRadius: 99,
                background: color,
                boxShadow: '0 0 0 1.5px #fff',
            }}
        />
        <Text size='xs' c='dimmed'>
            {label}
        </Text>
    </Group>
);

const boundMaps = new WeakSet<maplibregl.Map>();

const emptyCollection = (): MapCollection => ({ type: 'FeatureCollection', features: [] });

const districtColor = (color: string | null, index: number) =>
    color && HEX.test(color) ? color : DISTRICT_COLORS[index % DISTRICT_COLORS.length];

const zonePolygons = (district: CityContentMapRO['districts'][number]): number[][][][] => {
    if (district.polygons?.length) return district.polygons;
    if (district.rings?.length) return [district.rings];

    return [];
};

const hasZone = (district: CityContentMapRO['districts'][number]) =>
    zonePolygons(district).length > 0 || (district.lat != null && district.lng != null);

const ringCoords = (ring: number[][]): LngLat[] => {
    const points: LngLat[] = [];

    ring.forEach((pair) => {
        const lng = pair[0];
        const lat = pair[1];

        if (Number.isFinite(lng) && Number.isFinite(lat)) points.push([lng, lat]);
    });

    if (points.length >= 3) {
        const first = points[0];
        const last = points[points.length - 1];

        if (first[0] !== last[0] || first[1] !== last[1]) points.push(first);
    }

    return points;
};

const routeOpacity = (status: string, selected: boolean, dimmed: boolean) => {
    if (dimmed && !selected) return 0.28;
    if (status === 'rejected') return 0.45;

    return 0.92;
};

const ensureMapCss = () => {
    const id = 'city-content-map-css';
    const style = document.getElementById(id) ?? document.createElement('style');

    style.id = id;
    style.textContent = `
        .city-content-map.maplibregl-map { background: ${PAPER}; font-family: inherit; }
        .city-content-map .maplibregl-ctrl-logo { display: none; }
        .city-content-map-popup .maplibregl-popup-content {
            padding: 4px 8px;
            border-radius: 6px;
            color: ${ROUTE_LINE_COLOR};
            font: 600 12px/1.3 ui-sans-serif, system-ui, sans-serif;
        }
        .city-content-map-card .maplibregl-popup-content {
            padding: 0;
            width: 332px;
            border-radius: 14px;
            overflow: hidden;
            background: #fff;
            color: #1a1c1f;
            box-shadow: 0 12px 32px rgba(20, 24, 32, 0.22);
            font: 400 13px/1.45 ui-sans-serif, system-ui, sans-serif;
        }
        .city-content-map-card .maplibregl-popup-close-button {
            width: 26px;
            height: 26px;
            padding: 0;
            font-size: 18px;
            line-height: 24px;
            border-radius: 99px;
            right: 8px;
            top: 8px;
            color: #1a1c1f;
            background: rgba(255, 255, 255, 0.94);
        }
        .city-map-pin-scroll {
            max-height: var(--pin-card-max, min(48vh, 460px));
            overflow: auto;
            overscroll-behavior: contain;
        }
        .city-map-pin-hero {
            display: block;
            width: 100%;
            height: 168px;
            object-fit: cover;
            background: ${PAPER};
        }
        .city-map-pin-photos {
            display: flex;
            gap: 6px;
            overflow-x: auto;
            padding: 10px 10px 0;
        }
        .city-map-pin-photos img {
            width: 148px;
            height: 112px;
            flex: 0 0 auto;
            object-fit: cover;
            border-radius: 8px;
            background: ${PAPER};
        }
        .city-map-pin-credit {
            margin: 6px 14px 0;
            color: #6b7280;
            font-size: 11px;
        }
        .city-map-pin-body { padding: 12px 14px 16px; }
        .city-map-pin-body.is-bare { padding-top: 28px; }
        .city-map-pin-body h3 {
            margin: 0 28px 0 0;
            font-size: 16px;
            line-height: 1.3;
        }
        .city-map-pin-body p { margin: 0; }
        .city-map-pin-eyebrow {
            margin-bottom: 2px !important;
            color: #6b7280;
            font-size: 12px;
        }
        .city-map-pin-meta, .city-map-pin-address, .city-map-pin-known, .city-map-pin-credit {
            color: #6b7280;
        }
        .city-map-pin-meta, .city-map-pin-address { margin-top: 4px !important; }
        .city-map-pin-hook {
            margin-top: 10px !important;
            font-weight: 600;
        }
        .city-map-pin-known { margin-top: 4px !important; font-size: 12px; }
        .city-map-pin-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-top: 10px;
        }
        .city-map-pin-chips span {
            padding: 2px 8px;
            border-radius: 99px;
            background: #f3f4f6;
            font-size: 12px;
        }
        .city-map-pin-facts { margin-top: 10px; }
        .city-map-pin-facts p + p { margin-top: 3px; }
        .city-map-pin-section { margin-top: 14px; }
        .city-map-pin-section h4 {
            margin: 0 0 6px;
            color: #6b7280;
            font-size: 12px;
            font-weight: 700;
        }
        .city-map-pin-section ul { margin: 0; padding-left: 18px; }
        .city-map-pin-section li + li { margin-top: 3px; }
        .city-map-pin-cardlet {
            padding: 8px 10px;
            border-radius: 10px;
            background: #f6f7f9;
        }
        .city-map-pin-cardlet + .city-map-pin-cardlet { margin-top: 6px; }
        .city-map-pin-cardlet strong { display: block; margin-bottom: 2px; }
        .city-map-pin-story { white-space: pre-wrap; }
        .city-map-pin-audio { margin-top: 12px; }
        .city-map-pin-audio span {
            display: block;
            margin-bottom: 4px;
            color: #6b7280;
            font-size: 12px;
        }
        .city-map-pin-audio audio { width: 100%; height: 32px; }
        .city-map-pin-routes, .city-map-pin-links {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }
        .city-map-pin-routes button, .city-map-pin-links a {
            padding: 0;
            border: 0;
            background: none;
            color: #5b21b6;
            font: inherit;
            text-align: left;
            cursor: pointer;
            text-decoration: none;
        }
        .city-map-pin-routes button:hover, .city-map-pin-links a:hover { text-decoration: underline; }
    `;

    if (!style.parentElement) document.head.appendChild(style);
};

const setGeo = (map: maplibregl.Map, id: string, data: MapCollection) => {
    const source = map.getSource(id);

    if (source?.type !== 'geojson') return;

    (source as maplibregl.GeoJSONSource).setData(data as never);
};

const presentLayers = (map: maplibregl.Map) => INTERACTIVE.filter((id) => map.getLayer(id));

const readKindId = (
    feature: { properties?: Record<string, unknown> | null } | undefined,
    kinds: readonly string[],
): { kind: string; id: number } | null => {
    const kind = feature?.properties?.kind;
    const id = Number(feature?.properties?.id);

    if (typeof kind !== 'string' || !kinds.includes(kind)) return null;
    if (!Number.isFinite(id)) return null;

    return { kind, id };
};

const ensureCityLayers = (
    map: maplibregl.Map,
    actionsRef: { current: MapActions },
    detailOpenRef: { current: boolean },
) => {
    if (!map.getSource(LAYERS.districts)) {
        const before = map.getLayer(DISTRICT_FILL_BELOW_LAYER_ID)
            ? DISTRICT_FILL_BELOW_LAYER_ID
            : undefined;

        map.addSource(LAYERS.districts, { type: 'geojson', data: emptyCollection() as never });
        map.addSource(LAYERS.districtPoints, { type: 'geojson', data: emptyCollection() as never });
        map.addSource(LAYERS.routes, { type: 'geojson', data: emptyCollection() as never });
        map.addSource(LAYERS.routePoints, { type: 'geojson', data: emptyCollection() as never });
        map.addSource(LAYERS.places, { type: 'geojson', data: emptyCollection() as never });
        map.addSource(LAYERS.cafes, { type: 'geojson', data: emptyCollection() as never });

        map.addLayer(
            {
                id: LAYERS.districtFill,
                type: 'fill',
                source: LAYERS.districts,
                paint: {
                    'fill-color': ['get', 'color'],
                    'fill-opacity': ['get', 'opacity'],
                    'fill-outline-color': 'transparent',
                },
            },
            before,
        );
        map.addLayer({
            id: LAYERS.routeHit,
            type: 'line',
            source: LAYERS.routes,
            layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'sort'] },
            paint: { 'line-color': ROUTE_LINE_COLOR, 'line-width': 12, 'line-opacity': 0.01 },
        });
        map.addLayer({
            id: LAYERS.routes,
            type: 'line',
            source: LAYERS.routes,
            layout: { 'line-cap': 'round', 'line-join': 'round', 'line-sort-key': ['get', 'sort'] },
            paint: {
                'line-color': ROUTE_LINE_COLOR,
                'line-width': ['case', ['==', ['get', 'selected'], 1], 3, 2],
                'line-opacity': ['get', 'opacity'],
            },
        });
        map.addLayer({
            id: LAYERS.routePoints,
            type: 'circle',
            source: LAYERS.routePoints,
            paint: {
                'circle-radius': ['case', ['==', ['get', 'selected'], 1], 7, 5],
                'circle-color': ROUTE_LINE_COLOR,
                'circle-opacity': ['get', 'opacity'],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });
        map.addLayer({
            id: LAYERS.districtPoints,
            type: 'circle',
            source: LAYERS.districtPoints,
            paint: {
                'circle-radius': ['case', ['==', ['get', 'selected'], 1], 8, 6],
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.9,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
            },
        });
        map.addLayer({
            id: LAYERS.places,
            type: 'circle',
            source: LAYERS.places,
            layout: { visibility: 'none' },
            paint: {
                'circle-radius': [
                    'case',
                    ['==', ['get', 'selected'], 1],
                    8,
                    ['==', ['get', 'tone'], 'route'],
                    5.5,
                    4.5,
                ],
                'circle-color': ['match', ['get', 'tone'], 'route', PIN_ON_ROUTE, PIN_OFF_ROUTE],
                'circle-opacity': 0.96,
                'circle-stroke-width': ['case', ['==', ['get', 'selected'], 1], 2.5, 1.5],
                'circle-stroke-color': [
                    'case',
                    ['==', ['get', 'selected'], 1],
                    '#1A1C1F',
                    '#ffffff',
                ],
            },
        });
        map.addLayer({
            id: LAYERS.cafes,
            type: 'circle',
            source: LAYERS.cafes,
            layout: { visibility: 'none' },
            paint: {
                'circle-radius': ['case', ['==', ['get', 'selected'], 1], 8, 5.5],
                'circle-color': PIN_CAFE,
                'circle-opacity': ['case', ['==', ['get', 'tone'], 'closed'], 0.4, 0.96],
                'circle-stroke-width': ['case', ['==', ['get', 'selected'], 1], 2.5, 1.5],
                'circle-stroke-color': [
                    'case',
                    ['==', ['get', 'selected'], 1],
                    '#1A1C1F',
                    '#ffffff',
                ],
            },
        });
    }

    if (boundMaps.has(map)) return;

    boundMaps.add(map);

    const hoverPopup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 8,
        className: 'city-content-map-popup',
    });

    const topFeature = (point: maplibregl.PointLike) => {
        const layers = presentLayers(map);

        return layers.length ? map.queryRenderedFeatures(point, { layers })[0] : undefined;
    };

    const hover = (event: maplibregl.MapMouseEvent) => {
        const feature = topFeature(event.point);
        const kind = feature?.properties?.kind;
        const title = feature?.properties?.title;
        const pinHover = kind === 'place' || kind === 'cafe';

        if (pinHover && detailOpenRef.current) {
            map.getCanvas().style.cursor = 'pointer';
            hoverPopup.remove();

            return;
        }

        if (typeof title !== 'string' || !title) {
            map.getCanvas().style.cursor = '';
            hoverPopup.remove();

            return;
        }

        map.getCanvas().style.cursor = 'pointer';
        hoverPopup.setLngLat(event.lngLat).setText(title).addTo(map);
    };

    map.on('mousemove', hover);
    map.on('mouseout', () => {
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
    });
    map.on('click', (event) => {
        hoverPopup.remove();

        const feature = topFeature(event.point);
        const pin = readKindId(feature, ['place', 'cafe']);

        if (pin) {
            actionsRef.current.pin(pin.kind as 'place' | 'cafe', pin.id);

            return;
        }

        const hit = readKindId(feature, ['route', 'district']);

        if (hit) {
            actionsRef.current.select(hit.kind as 'route' | 'district', hit.id);

            return;
        }

        if (!feature) actionsRef.current.clear();
    });
};

export const CityContentMap = ({ cityId }: Props) => {
    const { t, i18n } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);
    const popupRef = useRef<maplibregl.Popup | null>(null);
    const dataRef = useRef<CityContentMapRO | null>(null);
    const fitKeyRef = useRef('');
    const focusRef = useRef('');
    const fittedHeightRef = useRef(0);
    const [mapReady, setMapReady] = useState(false);
    const [mapEpoch, setMapEpoch] = useState(0);
    const [framed, setFramed] = useState(false);
    const [data, setData] = useState<CityContentMapRO | null>(null);
    const [walked, setWalked] = useState<Record<number, LngLat[]>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showPlaces, setShowPlaces] = useState(true);
    const [showCafes, setShowCafes] = useState(false);
    const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
    const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
    const [selectedPin, setSelectedPin] = useState<SelectedPin | null>(null);
    const [popupHost, setPopupHost] = useState<HTMLDivElement | null>(null);
    const actionsRef = useRef<MapActions>({
        select: () => undefined,
        clear: () => undefined,
        pin: () => undefined,
    });
    const detailOpenRef = useRef(false);

    detailOpenRef.current = selectedPin != null;
    actionsRef.current = {
        select: (kind, id) => {
            setSelectedPin(null);

            if (kind === 'route') {
                setSelectedDistrictId(null);
                setSelectedRouteId((current) => (current === id ? null : id));

                return;
            }

            setSelectedRouteId(null);
            setSelectedDistrictId((current) => (current === id ? null : id));
        },
        clear: () => {
            setSelectedRouteId(null);
            setSelectedDistrictId(null);
            setSelectedPin(null);
        },
        pin: (kind, id) => {
            setSelectedPin((current) =>
                current && current.kind === kind && current.id === id ? null : { kind, id },
            );
        },
    };

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);
        setData(null);
        setFramed(false);
        setSelectedRouteId(null);
        setSelectedDistrictId(null);
        setSelectedPin(null);

        api.cities
            .contentMap(cityId)
            .then(({ data: next }) => {
                if (cancelled) return;
                setData(next);
            })
            .catch((loadError: unknown) => {
                if (cancelled) return;
                setError(apiErrorMessage(loadError));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [cityId]);

    useEffect(() => {
        if (!data) {
            setWalked({});

            return undefined;
        }

        let cancelled = false;
        const routes = data.routes.filter((route) => route.points.length >= 2);
        const next: Record<number, LngLat[]> = {};
        let cursor = 0;

        setWalked({});

        const worker = async () => {
            while (!cancelled) {
                const index = cursor;

                cursor += 1;

                if (index >= routes.length) return;

                const route = routes[index];
                const line = await fetchWalkingRoute(route.points.map(([lat, lng]) => [lng, lat]));

                if (cancelled || !line || line.length < 2) continue;

                next[route.id] = line;
                setWalked({ ...next });
            }
        };

        void Promise.all([worker(), worker(), worker()]);

        return () => {
            cancelled = true;
        };
    }, [data]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return undefined;

        let cancelled = false;
        let map: maplibregl.Map | null = null;
        let observer: ResizeObserver | null = null;

        ensureMapCss();
        setFramed(false);

        void getRoutellersMapStyle(i18n.language)
            .then((style) => {
                if (cancelled || !containerRef.current) return;

                map = new maplibregl.Map({
                    container: containerRef.current,
                    style: style as maplibregl.StyleSpecification,
                    center: [0, 20],
                    zoom: 2,
                    attributionControl: false,
                    dragRotate: false,
                    pitchWithRotate: false,
                    touchPitch: false,
                    maxPitch: 0,
                    canvasContextAttributes: { antialias: true },
                });
                map.touchZoomRotate.disableRotation();
                map.addControl(
                    new maplibregl.NavigationControl({
                        showCompass: false,
                        visualizePitch: false,
                    }),
                    'top-right',
                );
                map.addControl(
                    new maplibregl.AttributionControl({ compact: true }),
                    'bottom-right',
                );
                mapRef.current = map;

                observer = new ResizeObserver(() => {
                    if (cancelled || !map) return;

                    map.resize();
                    const height = containerRef.current?.clientHeight || 0;

                    if (height > 80 && fittedHeightRef.current < 80) {
                        setMapEpoch((epoch) => epoch + 1);
                    }
                });
                observer.observe(containerRef.current);

                map.on('load', () => {
                    if (cancelled || !map) return;

                    map.resize();
                    setMapReady(true);
                });
            })
            .catch(() => {
                if (!cancelled) setError(t('pages.cityDetail.map.leafletFailed'));
            });

        return () => {
            cancelled = true;
            setMapReady(false);
            dataRef.current = null;
            fitKeyRef.current = '';
            focusRef.current = '';
            fittedHeightRef.current = 0;
            observer?.disconnect();
            map?.remove();
            mapRef.current = null;
        };
        // The basemap is rebuilt only when the label language changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        const map = mapRef.current;

        if (!mapReady || !map || !data) return;

        if (dataRef.current !== data) {
            dataRef.current = data;
            fitKeyRef.current = '';
            focusRef.current = '';
            fittedHeightRef.current = 0;
        }

        if (mapEpoch > 0 && fittedHeightRef.current < 80) {
            fitKeyRef.current = '';
            focusRef.current = '';
        }

        ensureCityLayers(map, actionsRef, detailOpenRef);
        map.resize();

        const districts = emptyCollection();
        const districtPoints = emptyCollection();

        data.districts.forEach((district, index) => {
            const color = districtColor(district.color, index);
            const title = district.title || t('pages.cityDetail.map.untitledDistrict');
            const selected = district.id === selectedDistrictId;
            const label =
                district.status === 'published'
                    ? title
                    : `${title} · ${enumLabel(t, 'enums.guideStatus', district.status)}`;
            const polygons = zonePolygons(district)
                .map((rings) => rings.map(ringCoords).filter((ring) => ring.length >= 4))
                .filter((rings) => rings.length > 0);

            if (polygons.length) {
                for (const rings of polygons) {
                    districts.features.push({
                        type: 'Feature',
                        properties: {
                            kind: 'district',
                            id: district.id,
                            title: label,
                            color,
                            opacity: selected
                                ? 0.85
                                : district.status === 'published'
                                  ? DISTRICT_WASH_OPACITY
                                  : 0.28,
                        },
                        geometry: { type: 'Polygon', coordinates: rings },
                    });
                }

                return;
            }

            if (district.lat == null || district.lng == null) return;

            districtPoints.features.push({
                type: 'Feature',
                properties: {
                    kind: 'district',
                    id: district.id,
                    title: label,
                    color,
                    selected: selected ? 1 : 0,
                },
                geometry: { type: 'Point', coordinates: [district.lng, district.lat] },
            });
        });

        const routes = emptyCollection();
        const routePoints = emptyCollection();
        const somethingSelected = selectedRouteId != null;

        data.routes.forEach((route) => {
            if (!route.points.length) return;

            const title = route.title || t('pages.cityDetail.map.untitledRoute', { id: route.id });
            const label =
                route.status === 'published'
                    ? title
                    : `${title} · ${enumLabel(t, 'enums.readyRouteStatus', route.status)}`;
            const selected = route.id === selectedRouteId;
            const opacity = routeOpacity(route.status, selected, somethingSelected);
            const straight = route.points.map(([lat, lng]) => [lng, lat] as LngLat);
            const line = walked[route.id]?.length >= 2 ? walked[route.id] : straight;

            if (line.length >= 2) {
                routes.features.push({
                    type: 'Feature',
                    properties: {
                        kind: 'route',
                        id: route.id,
                        title: label,
                        opacity,
                        selected: selected ? 1 : 0,
                        sort: selected ? 1 : 0,
                    },
                    geometry: { type: 'LineString', coordinates: line },
                });

                return;
            }

            routePoints.features.push({
                type: 'Feature',
                properties: {
                    kind: 'route',
                    id: route.id,
                    title: label,
                    opacity,
                    selected: selected ? 1 : 0,
                },
                geometry: { type: 'Point', coordinates: line[0] },
            });
        });

        const places = emptyCollection();
        const cafes = emptyCollection();
        const onRoute = new Set<number>();

        data.routes.forEach((route) => {
            (route.placeIds || []).forEach((placeId) => {
                onRoute.add(placeId);
            });
        });

        [...data.places]
            .sort((left, right) => Number(onRoute.has(left.id)) - Number(onRoute.has(right.id)))
            .forEach((place) => {
                const onARoute = onRoute.has(place.id);

                places.features.push({
                    type: 'Feature',
                    properties: {
                        kind: 'place',
                        id: place.id,
                        title: place.title,
                        tone: onARoute ? 'route' : 'free',
                        selected:
                            selectedPin?.kind === 'place' && selectedPin.id === place.id ? 1 : 0,
                    },
                    geometry: { type: 'Point', coordinates: [place.lng, place.lat] },
                });
            });
        data.cafes.forEach((cafe) => {
            cafes.features.push({
                type: 'Feature',
                properties: {
                    kind: 'cafe',
                    id: cafe.id,
                    title: cafe.title,
                    tone: cafe.status,
                    selected: selectedPin?.kind === 'cafe' && selectedPin.id === cafe.id ? 1 : 0,
                },
                geometry: { type: 'Point', coordinates: [cafe.lng, cafe.lat] },
            });
        });

        setGeo(map, LAYERS.districts, districts);
        setGeo(map, LAYERS.districtPoints, districtPoints);
        setGeo(map, LAYERS.routes, routes);
        setGeo(map, LAYERS.routePoints, routePoints);
        setGeo(map, LAYERS.places, places);
        setGeo(map, LAYERS.cafes, cafes);
        map.setLayoutProperty(LAYERS.places, 'visibility', showPlaces ? 'visible' : 'none');
        map.setLayoutProperty(LAYERS.cafes, 'visibility', showCafes ? 'visible' : 'none');

        const overview = new maplibregl.LngLatBounds();

        data.districts.forEach((district) => {
            const polygons = zonePolygons(district);

            polygons.forEach((polygon) => {
                polygon.forEach((ring) => {
                    ringCoords(ring).forEach(([lng, lat]) => {
                        overview.extend([lng, lat]);
                    });
                });
            });

            if (!polygons.length && district.lat != null && district.lng != null) {
                overview.extend([district.lng, district.lat]);
            }
        });
        data.routes.forEach((route) => {
            route.points.forEach(([lat, lng]) => {
                overview.extend([lng, lat]);
            });
        });

        const fitBounds = (bounds: maplibregl.LngLatBounds, animate: boolean) => {
            if (bounds.isEmpty()) {
                if (data.center)
                    map.jumpTo({ center: [data.center.lng, data.center.lat], zoom: 13 });
            } else {
                map.fitBounds(bounds, { padding: 28, maxZoom: 15, duration: animate ? 400 : 0 });
            }

            fittedHeightRef.current = containerRef.current?.clientHeight || 0;
        };

        const fitKey = overview.isEmpty() ? `pins:${showPlaces}:${showCafes}` : 'overview';
        const walkedFocus = selectedRouteId != null && (walked[selectedRouteId]?.length ?? 0) >= 2;
        const focusKey =
            selectedRouteId != null
                ? `route:${selectedRouteId}:${walkedFocus ? 'walk' : 'stops'}`
                : selectedDistrictId != null
                  ? `district:${selectedDistrictId}`
                  : '';

        if (focusKey && focusKey !== focusRef.current) {
            if (selectedRouteId != null) {
                const route = data.routes.find((item) => item.id === selectedRouteId);
                const bounds = new maplibregl.LngLatBounds();
                const line = route && walked[route.id]?.length >= 2 ? walked[route.id] : null;

                if (line) {
                    line.forEach(([lng, lat]) => {
                        bounds.extend([lng, lat]);
                    });
                } else {
                    route?.points.forEach(([lat, lng]) => {
                        bounds.extend([lng, lat]);
                    });
                }

                if (!bounds.isEmpty()) fitBounds(bounds, true);
            } else if (selectedDistrictId != null) {
                const district = data.districts.find((item) => item.id === selectedDistrictId);
                const bounds = new maplibregl.LngLatBounds();

                const polygons = district ? zonePolygons(district) : [];

                polygons.forEach((polygon) => {
                    polygon.forEach((ring) => {
                        ringCoords(ring).forEach(([lng, lat]) => {
                            bounds.extend([lng, lat]);
                        });
                    });
                });

                if (district?.lat != null && district.lng != null && !polygons.length) {
                    bounds.extend([district.lng, district.lat]);
                }

                if (!bounds.isEmpty()) fitBounds(bounds, true);
            }

            focusRef.current = focusKey;
        } else if (!focusKey && focusRef.current) {
            focusRef.current = '';
        } else if (fitKey !== fitKeyRef.current) {
            if (!overview.isEmpty()) {
                fitBounds(overview, false);
            } else {
                const pins = new maplibregl.LngLatBounds();

                if (showPlaces) {
                    data.places.forEach((place) => {
                        pins.extend([place.lng, place.lat]);
                    });
                }
                if (showCafes) {
                    data.cafes.forEach((cafe) => {
                        pins.extend([cafe.lng, cafe.lat]);
                    });
                }
                fitBounds(pins, false);
            }

            fitKeyRef.current = fitKey;
        }

        setFramed(true);
    }, [
        data,
        mapEpoch,
        mapReady,
        selectedDistrictId,
        selectedPin,
        selectedRouteId,
        showCafes,
        showPlaces,
        t,
        walked,
    ]);

    useEffect(() => {
        const map = mapRef.current;
        const pin = selectedPin;
        const item =
            pin && data
                ? pin.kind === 'place'
                    ? data.places.find((place) => place.id === pin.id)
                    : data.cafes.find((cafe) => cafe.id === pin.id)
                : null;

        if (!mapReady || !map || !pin || !item) {
            setPopupHost(null);

            return undefined;
        }

        const host = document.createElement('div');
        const room = Math.max(220, Math.min(460, map.getContainer().clientHeight - 72));

        host.style.setProperty('--pin-card-max', `${room}px`);

        const stopGesture = (event: Event) => {
            event.stopPropagation();
        };

        host.addEventListener('wheel', stopGesture);
        host.addEventListener('dblclick', stopGesture);
        host.addEventListener('mousedown', stopGesture);
        host.addEventListener('touchstart', stopGesture);

        let removed = false;
        const popup = new maplibregl.Popup({
            closeButton: true,
            closeOnClick: false,
            closeOnMove: false,
            focusAfterOpen: false,
            maxWidth: '332px',
            offset: 16,
            className: 'city-content-map-card',
        })
            .setLngLat([item.lng, item.lat])
            .setDOMContent(host)
            .addTo(map);

        popup.on('close', () => {
            if (removed) return;

            setSelectedPin((current) =>
                current && current.kind === pin.kind && current.id === pin.id ? null : current,
            );
        });
        popupRef.current = popup;
        setPopupHost(host);

        return () => {
            removed = true;
            popupRef.current = null;
            popup.remove();
            setPopupHost(null);
        };
    }, [data, mapReady, selectedPin]);

    useLayoutEffect(() => {
        const popup = popupRef.current;

        if (!popup || !popupHost) return undefined;

        const reposition = () => {
            const current = popupRef.current;

            if (!current?.isOpen()) return;

            current.setLngLat(current.getLngLat());
        };

        reposition();

        const observer = new ResizeObserver(reposition);

        observer.observe(popupHost);

        return () => observer.disconnect();
    }, [popupHost]);

    const drawnDistricts = data?.districts.filter(hasZone).length ?? 0;
    const countLabel = (label: string, count: number | null) =>
        count == null ? label : `${label} (${count})`;
    const selectedPlace =
        selectedPin?.kind === 'place'
            ? data?.places.find((place) => place.id === selectedPin.id)
            : undefined;
    const selectedCafe =
        selectedPin?.kind === 'cafe'
            ? data?.cafes.find((cafe) => cafe.id === selectedPin.id)
            : undefined;
    const pinRoutes =
        selectedPlace && data
            ? data.routes
                  .filter((route) => (route.placeIds || []).includes(selectedPlace.id))
                  .map((route) => ({
                      id: route.id,
                      title:
                          route.title || t('pages.cityDetail.map.untitledRoute', { id: route.id }),
                      status: route.status,
                  }))
            : [];

    return (
        <Stack gap='sm' style={{ height: 'calc(100dvh - 280px)', minHeight: 520 }}>
            <Group justify='space-between' align='center' wrap='wrap' gap='sm'>
                <Group gap='lg'>
                    <Switch
                        label={countLabel(
                            t('pages.cityDetail.map.places'),
                            data ? data.places.length : null,
                        )}
                        checked={showPlaces}
                        onChange={(event) => setShowPlaces(event.currentTarget.checked)}
                    />
                    <Switch
                        label={countLabel(
                            t('pages.cityDetail.map.cafes'),
                            data ? data.cafes.length : null,
                        )}
                        checked={showCafes}
                        onChange={(event) => setShowCafes(event.currentTarget.checked)}
                    />
                    <Group gap='sm'>
                        <LegendDot color={PIN_ON_ROUTE} label={t('pages.cityDetail.map.inRoute')} />
                        <LegendDot
                            color={PIN_OFF_ROUTE}
                            label={t('pages.cityDetail.map.notInRoute')}
                        />
                        <LegendDot color={PIN_CAFE} label={t('pages.cityDetail.map.cafes')} />
                    </Group>
                </Group>
                {data && (
                    <Text size='sm' c='dimmed'>
                        {t('pages.cityDetail.map.summary', {
                            districts: data.districts.length,
                            routes: data.routes.length,
                        })}
                    </Text>
                )}
            </Group>

            {data && data.districts.length > 0 && (
                <Group gap={6} wrap='wrap' style={{ maxHeight: 72, overflow: 'auto' }}>
                    {data.districts.map((district, index) => {
                        const title = district.title || t('pages.cityDetail.map.untitledDistrict');
                        const bits = [title];

                        if (!hasZone(district)) bits.push(t('pages.cityDetail.map.noOutline'));
                        else if (district.status !== 'published') {
                            bits.push(enumLabel(t, 'enums.guideStatus', district.status));
                        }

                        return (
                            <Button
                                key={district.id}
                                type='button'
                                size='xs'
                                variant={selectedDistrictId === district.id ? 'filled' : 'light'}
                                color='gray'
                                onClick={() => {
                                    setSelectedPin(null);
                                    setSelectedRouteId(null);
                                    setSelectedDistrictId((current) =>
                                        current === district.id ? null : district.id,
                                    );
                                }}
                                leftSection={
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 2,
                                            background: districtColor(district.color, index),
                                        }}
                                    />
                                }
                                title={bits.join(' · ')}
                                style={{ maxWidth: 280 }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {bits.join(' · ')}
                                </span>
                            </Button>
                        );
                    })}
                </Group>
            )}

            {data && data.routes.length > 0 && (
                <Group gap={6} wrap='wrap' style={{ maxHeight: 92, overflow: 'auto' }}>
                    {data.routes.map((route) => {
                        const title =
                            route.title ||
                            t('pages.cityDetail.map.untitledRoute', { id: route.id });
                        const bits = [title];

                        if (route.status !== 'published') {
                            bits.push(enumLabel(t, 'enums.readyRouteStatus', route.status));
                        }

                        if (route.points.length === 0) bits.push(t('pages.cityDetail.map.noLine'));

                        return (
                            <Button
                                key={route.id}
                                type='button'
                                size='xs'
                                variant={selectedRouteId === route.id ? 'filled' : 'light'}
                                color='gray'
                                onClick={() => {
                                    setSelectedPin(null);
                                    setSelectedDistrictId(null);
                                    setSelectedRouteId((current) =>
                                        current === route.id ? null : route.id,
                                    );
                                }}
                                leftSection={
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 99,
                                            background: ROUTE_LINE_COLOR,
                                            opacity: route.status === 'rejected' ? 0.45 : 1,
                                        }}
                                    />
                                }
                                title={bits.join(' · ')}
                                style={{ maxWidth: 280 }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {bits.join(' · ')}
                                </span>
                            </Button>
                        );
                    })}
                </Group>
            )}

            <Box style={{ position: 'relative', flex: 1, minHeight: 320 }}>
                <Box
                    ref={containerRef}
                    className='city-content-map'
                    style={{
                        height: '100%',
                        borderRadius: 12,
                        overflow: 'hidden',
                        border: '1px solid var(--mantine-color-gray-3)',
                        background: PAPER,
                    }}
                />
                {!error && (loading || !framed) && (
                    <Center
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(232,233,237,.78)',
                        }}
                    >
                        <Loader />
                    </Center>
                )}
                {!loading &&
                    framed &&
                    !error &&
                    data &&
                    drawnDistricts === 0 &&
                    data.routes.every((route) => route.points.length === 0) &&
                    !(showPlaces && data.places.length) &&
                    !(showCafes && data.cafes.length) && (
                        <Center
                            style={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                            }}
                        >
                            <Text
                                size='sm'
                                c='dimmed'
                                bg='white'
                                px='sm'
                                py={4}
                                style={{ borderRadius: 8 }}
                            >
                                {t('pages.cityDetail.map.empty')}
                            </Text>
                        </Center>
                    )}
            </Box>

            {error && (
                <Text size='sm' c='red'>
                    {error}
                </Text>
            )}
            {popupHost && selectedPlace
                ? createPortal(
                      <CityMapPinCard
                          kind='place'
                          place={selectedPlace}
                          routes={pinRoutes}
                          onShowRoute={(id) => {
                              setSelectedDistrictId(null);
                              setSelectedRouteId(id);
                          }}
                      />,
                      popupHost,
                  )
                : null}
            {popupHost && selectedCafe
                ? createPortal(<CityMapPinCard kind='cafe' cafe={selectedCafe} />, popupHost)
                : null}
        </Stack>
    );
};
